import React, { useState, useEffect, useCallback } from 'react';
import { fetchDelayBreakdown, openDelayInHub, setDelayBadge } from '../services/hubDelay';
import {
  sourceLabel, fmtMin, fmtMoney, fmtWhen, largerOfText, readAsText, reportedText, REPORT_SOURCE,
  billableHeadline, stopKindLabel, errorText,
} from '../utils/delayBreakdown';

// __DELAY_VIEW__ (2026-10-30) Load Details -> Delay: one job's delay by type, read-only. Everything billable is the
// hub's own billing calculation (GET /api/board/delay-breakdown/<id>); "Recorded only" is shown for review and never
// added. Adjustments happen in the hub's Hauling Board tab ("Open in Hauling Board tab").

const chip = (bg) => ({ display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '3px', fontSize: '0.68rem', fontWeight: 'bold',
  letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--bg-primary)', backgroundColor: bg, marginRight: '0.5rem' });
const BILLABLE = chip('var(--color-success)');
const RECORDED = chip('var(--text-muted)');
const box = { backgroundColor: 'var(--surface-2)', padding: '0.9rem 1rem', borderRadius: '5px', marginBottom: '1rem' };
const h = { color: 'var(--accent-primary)', margin: '0 0 0.6rem 0', fontSize: '0.95rem' };
const small = { color: 'var(--text-muted)', fontSize: '0.78rem' };
const th = { textAlign: 'left', padding: '0.3rem 0.4rem', color: 'var(--text-muted)', fontWeight: 'normal', fontSize: '0.75rem', whiteSpace: 'nowrap' };
const td = { padding: '0.35rem 0.4rem', borderTop: '1px solid var(--border-default)', fontSize: '0.83rem', color: 'var(--text-primary)', verticalAlign: 'top' };
const btn = { border: '1px solid var(--border-default)', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem',
  padding: '0.35rem 0.75rem', backgroundColor: 'var(--btn-secondary-bg)', color: 'var(--text-primary)' };

function Part({ label, value, sub, strong }) {
  return (
    <div style={{ flex: '1 1 140px', minWidth: 0 }}>
      <div style={small}>{label}</div>
      <div style={{ fontSize: strong ? '1.15rem' : '1rem', fontWeight: 'bold', color: strong ? 'var(--accent-primary)' : 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={small}>{sub}</div>}
    </div>
  );
}

function TimeCell({ at, src }) {
  if (!at) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  return (
    <div>
      <div>{fmtWhen(at)}</div>
      {src && <div style={{ ...small, fontSize: '0.7rem' }}>{sourceLabel(src)}</div>}
    </div>
  );
}

function DelayBreakdownPanel({ loadId }) {
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const r = await fetchDelayBreakdown(loadId);
    if (r.ok) {
      setState({ loading: false, view: r.view });
      setDelayBadge(loadId, (r.view && r.view.billable && r.view.billable.delay_time_min) || 0);
    } else setState({ loading: false, error: errorText(r) });
  }, [loadId]);

  useEffect(() => { load(); }, [load]);

  if (state.loading && !state.view) return <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Reading the delay from the hub…</div>;
  if (state.error) return <div style={{ ...box, color: 'var(--text-secondary)' }}>{state.error}</div>;
  const v = state.view || {};
  const b = v.billable || {};
  const t = v.trello || {};
  const ro = v.recorded_only || {};
  const rep = ro.driver_reported;
  const stops = v.stops || [];

  return (
    <div data-delay-panel={v.load_number || ''}>
      {/* Billable */}
      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          <div>
            <span style={BILLABLE}>Billable</span>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--accent-primary)' }}>{billableHeadline(b)}</span>
            <div style={{ ...small, marginTop: '0.25rem' }}>
              {b.rule || 'Delay Time = larger of (booked delay, GPS billable dwell) + road delay'}
              {b.delay_rate != null && <> · at {fmtMoney(b.delay_rate)}/min, before fuel surcharge / premium</>}
              {v.billed && <> · <b>already billed</b></>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button style={btn} onClick={() => openDelayInHub(loadId, 'delay')} title="Adjust a shared stop's split or an address fix's time lost in the hub">Open in Hauling Board tab</button>
            <button style={btn} onClick={load} disabled={state.loading}>{state.loading ? '…' : 'Refresh'}</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Part label="Booked delay" value={fmtMin(b.booked_min)} sub="booking / quote form" />
          <Part label="GPS billable dwell" value={fmtMin(b.gps_billable_min)} sub={'past ' + fmtMin(b.free_min) + ' free per stop + shared stops + fix time'} />
          <Part label="Road delay" value={b.road_min == null ? 'not measured' : fmtMin(b.road_min)}
            sub={b.road_min ? ('driving over plan' + (b.road_equiv_min && Math.round(b.road_equiv_min) !== Math.round(b.road_min) ? ' = ' + fmtMin(b.road_equiv_min) + ' at the delay rate' : '')) : 'drives between this job\'s stops'} />
          <Part label="Billable total" value={billableHeadline(b)} strong />
        </div>
        <div style={{ ...small, marginTop: '0.5rem' }}>{largerOfText(b)}. The driver sees {fmtMin(b.total_delay_min)} total delay on the job.</div>
      </div>

      {/* Per stop */}
      <div style={box}>
        <h3 style={h}>Per stop</h3>
        {stops.length === 0 ? <div style={small}>No stops recorded.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Stop</th><th style={th}>Arrived</th><th style={th}>Left</th>
                  <th style={th}>On site</th><th style={th}>Free</th><th style={th}>Past free</th><th style={th} />
                </tr>
              </thead>
              <tbody>
                {stops.map((s) => (
                  <tr key={s.stop_id} data-stop-billable={s.billable ? '1' : '0'}>
                    <td style={{ ...td, maxWidth: '200px' }}>
                      <div style={{ fontSize: '0.7rem', color: s.driver_stop ? 'var(--text-muted)' : s.stop_type === 'pickup' ? 'var(--color-warning)' : 'var(--color-success)', textTransform: 'uppercase' }}>{stopKindLabel(s)}</div>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.address || ''}>{s.place || s.address || '—'}</div>
                      {s.road && s.road.kind === 'same_job' && (
                        <div style={{ ...small, fontSize: '0.72rem' }}>
                          road in: {fmtMin(s.road.actual_min)} vs {fmtMin(s.road.planned_min)} planned
                          {s.road.over_min ? <> · <b style={{ color: s.road.billed ? 'var(--color-warning)' : 'var(--text-muted)' }}>{s.road.over_min} over{s.road.billed ? ' (billed)' : ' (under tolerance)'}</b></> : null}
                        </div>
                      )}
                      {s.road && s.road.kind === 'cross_job' && <div style={{ ...small, fontSize: '0.72rem' }}>road in from another job's stop: recorded, never billed</div>}
                      {(s.fixes || []).map((f, i) => (
                        <div key={i} style={{ ...small, fontSize: '0.72rem', color: 'var(--color-warning)' }}>
                          ✎ address fixed{f.time_lost_min != null ? ' · time lost ' + fmtMin(f.time_lost_min) : f.answered ? '' : ' · time lost not answered yet'}
                          {f.time_lost_min ? (f.billable ? ' (billable)' : ' (not billable)') : ''}
                        </div>
                      ))}
                      {Array.isArray(s.shared) && s.shared.length > 1 && (
                        <div style={{ ...small, fontSize: '0.72rem' }}>shared visit: {s.shared.map((x) => (x.this_load ? 'this job' : (x.load_number || 'other job')) + ' ' + Math.round(x.share_pct || 0) + '%').join(' / ')}</div>
                      )}
                    </td>
                    <td style={td}><TimeCell at={s.arrived} src={s.arrived_source} /></td>
                    <td style={td}><TimeCell at={s.left} src={s.left_source} /></td>
                    <td style={td}>{fmtMin(s.on_site_min)}</td>
                    <td style={td}>{fmtMin(s.free_min)}</td>
                    <td style={{ ...td, fontWeight: 'bold' }}>{fmtMin(s.past_free_min)}</td>
                    <td style={td}>{s.billable ? <span style={BILLABLE}>Billable</span> : s.driver_stop ? <span style={RECORDED}>Driver's stop</span> : <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>within free time</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Written to Trello */}
      <div style={box}>
        <h3 style={h}>Written to Trello</h3>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
          Delay Time on the Trucking card and every HM Accounting copy: <b>{t.writes_delay_time ? t.writes_delay_time + ' min' : 'nothing to write'}</b>
          <span style={small}> (raised only, never lowered; completed jobs not yet billed)</span>
        </div>
        <div style={{ ...small, marginTop: '0.3rem' }}>
          Last write: {t.last_write_at ? <>{fmtWhen(t.last_write_at)} — {t.last_write}</> : 'none yet'}
          {t.last_pass_at && <> · last pass {fmtWhen(t.last_pass_at)}</>}
        </div>
        {(t.cards || []).length > 0 && (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            {t.cards.map((c) => (
              <div key={c.card_id} style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', border: '1px solid var(--border-default)', borderRadius: '5px' }}>
                {c.where}: {c.read === 'ok' ? (c.has_field === false ? 'no Delay Time field' : (c.delay_time == null ? 'empty' : Math.round(c.delay_time) + ' min')) : c.read === 'gone' ? 'card gone' : 'could not read'}
              </div>
            ))}
          </div>
        )}
        {(t.card_higher || []).map((x, i) => <div key={i} style={{ ...small, color: 'var(--color-warning)', marginTop: '0.3rem' }}>Card kept higher: {x.why}</div>)}
      </div>

      {/* Recorded only */}
      <div style={box}>
        <h3 style={h}><span style={RECORDED}>Recorded only</span>for review - never added on top of the billable total</h3>
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>Driver-reported times</div>
          {rep && reportedText(rep) ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              {reportedText(rep)}
              <span style={small}> — {rep.by || 'driver'}{rep.at ? ', ' + fmtWhen(rep.at) : ''}{rep.source ? ' (' + (REPORT_SOURCE[rep.source] || rep.source) + ')' : ''}</span>
              {(rep.history || []).length > 1 && (
                <div style={small}>{rep.history.map((x, i) => <span key={i}>{i ? ' · ' : ''}{x.kind} {x.minutes} min by {x.by || '?'} {fmtWhen(x.at)}</span>)}</div>
              )}
            </div>
          ) : <div style={small}>None.</div>}
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>Trello comments that mention time</div>
          {(ro.comments || []).length === 0 ? <div style={small}>None found on the cards.</div> : ro.comments.map((c, i) => (
            <blockquote key={i} style={{ margin: '0.35rem 0', padding: '0.4rem 0.6rem', borderLeft: '3px solid var(--border-default)', backgroundColor: 'var(--surface-1)', borderRadius: '3px' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>“{c.text}”</div>
              <div style={small}>{c.by || 'someone'} · {fmtWhen(c.at)} · {c.where}{readAsText(c.read_as) ? ' · read as ' + readAsText(c.read_as) : ''}</div>
            </blockquote>
          ))}
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>Delay analyzer</div>
            <button style={{ ...btn, fontSize: '0.75rem', padding: '0.2rem 0.6rem' }} onClick={() => openDelayInHub(loadId, 'analyzer')}>Open the delay analyzer</button>
          </div>
          {(ro.analyzer || []).length > 0 && <div style={small}>What it applied is the job's saved delay (Booked delay above) - listed here for who and when, never added again.</div>}
          {(ro.analyzer || []).length === 0 ? <div style={small}>No analyzer result applied to this job yet.</div> : ro.analyzer.map((a, i) => (
            <div key={i} style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              {a.minutes} min applied<span style={small}> — {a.by || '?'}, {fmtWhen(a.at)}{a.was != null ? ' (was ' + a.was + ')' : ''}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DelayBreakdownPanel;
